import { SCHEMA_VERSION } from '@portfolio-pm/shared';
import type * as Y from 'yjs';
import {
  ROOT_KEYS,
  ensureDefaultWorkflowColumns,
  ensureReminderPreferences,
  ensureUiPreferences,
  getMetaMap,
  getSchemaVersion,
} from './doc.ts';

/**
 * A forward-only migration. `apply` is a pure function over the Y.Doc that
 * mutates it so it matches the shape expected at version `to`. Migrations
 * are chained: the runner walks them in order and bumps `meta.schemaVersion`
 * after each step.
 *
 * Rules of engagement:
 * - Never reuse a field name with a different type.
 * - Migrations must be idempotent under retry, because Yjs observers may
 *   fire mid-migration.
 * - Tests for each migration live alongside it.
 */
export interface Migration {
  from: number;
  to: number;
  apply: (doc: Y.Doc) => void;
}

export interface RunMigrationsOptions {
  targetVersion: number;
}

export function runMigrations(
  doc: Y.Doc,
  migrations: readonly Migration[],
  options: RunMigrationsOptions,
): void {
  const { targetVersion } = options;
  const current = getSchemaVersion(doc) ?? 0;

  if (current === targetVersion) return;
  if (current > targetVersion) {
    throw new Error(
      `refusing to downgrade Yjs doc from schemaVersion ${current} to ${targetVersion}`,
    );
  }

  // Build an index for O(1) lookup.
  const byFrom = new Map<number, Migration>();
  for (const m of migrations) {
    byFrom.set(m.from, m);
  }

  let version = current;
  doc.transact(() => {
    while (version < targetVersion) {
      const step = byFrom.get(version);
      if (!step) {
        throw new Error(`missing migration from schemaVersion ${version}`);
      }
      step.apply(doc);
      version = step.to;
      getMetaMap(doc).set('schemaVersion', version);
    }
  });
}

export const DEFAULT_MIGRATIONS: readonly Migration[] = [
  {
    from: 1,
    to: 2,
    apply: (doc) => {
      for (const key of ROOT_KEYS) doc.getMap(key);

      const baseTs = Number(getMetaMap(doc).get('createdAt') ?? 0);
      const objectives = doc.getMap<Y.Map<unknown>>('objectives');
      let objectiveOrder = 0;
      for (const inner of objectives.values()) {
        if (!inner.has('status')) inner.set('status', 'on_track');
        if (!inner.has('order')) inner.set('order', objectiveOrder++);
        if (!inner.has('createdAt')) inner.set('createdAt', baseTs);
        if (!inner.has('updatedAt')) inner.set('updatedAt', baseTs);
      }

      const keyResults = doc.getMap<Y.Map<unknown>>('keyResults');
      let keyResultOrder = 0;
      for (const inner of keyResults.values()) {
        const mode = String(inner.get('progressMode') ?? 'manual');
        if (!inner.has('progressMode')) inner.set('progressMode', mode);
        if (!inner.has('manualProgress') && mode === 'manual') inner.set('manualProgress', 0);
        if (!inner.has('startValue') && mode === 'numeric') inner.set('startValue', 0);
        if (!inner.has('currentValue') && mode === 'numeric') inner.set('currentValue', 0);
        if (!inner.has('targetValue') && mode === 'numeric') inner.set('targetValue', 0);
        if (!inner.has('order')) inner.set('order', keyResultOrder++);
        if (!inner.has('createdAt')) inner.set('createdAt', baseTs);
        if (!inner.has('updatedAt')) inner.set('updatedAt', baseTs);
      }
    },
  },
  {
    from: 2,
    to: 3,
    apply: (doc) => {
      for (const key of ROOT_KEYS) doc.getMap(key);

      const baseTs = Number(getMetaMap(doc).get('createdAt') ?? 0);
      ensureReminderPreferences(doc, baseTs);

      const tasks = doc.getMap<Y.Map<unknown>>('tasks');
      for (const inner of tasks.values()) {
        if (!inner.has('updatedAt')) inner.set('updatedAt', baseTs);
      }
    },
  },
  {
    from: 3,
    to: 4,
    apply: (doc) => {
      for (const key of ROOT_KEYS) doc.getMap(key);

      const tasks = doc.getMap<Y.Map<unknown>>('tasks');
      for (const inner of tasks.values()) {
        if (!inner.has('workflowState')) inner.set('workflowState', 'backlog');
      }
    },
  },
  {
    from: 4,
    to: 5,
    apply: (doc) => {
      for (const key of ROOT_KEYS) doc.getMap(key);

      const baseTs = Number(getMetaMap(doc).get('createdAt') ?? 0);
      ensureDefaultWorkflowColumns(doc, baseTs);
    },
  },
  {
    from: 5,
    to: 6,
    apply: (doc) => {
      for (const key of ROOT_KEYS) doc.getMap(key);

      const tasks = doc.getMap<Y.Map<unknown>>('tasks');
      for (const inner of tasks.values()) {
        if (inner.has('dueAt') && !inner.has('dueAtHasTime')) inner.set('dueAtHasTime', true);
        if (inner.has('scheduledAt') && !inner.has('scheduledAtHasTime')) {
          inner.set('scheduledAtHasTime', true);
        }
      }
    },
  },
  {
    from: 6,
    to: 7,
    apply: (doc) => {
      for (const key of ROOT_KEYS) doc.getMap(key);

      const baseTs = Number(getMetaMap(doc).get('createdAt') ?? 0);
      ensureUiPreferences(doc, baseTs);
    },
  },
  {
    from: 7,
    to: 8,
    apply: (doc) => {
      for (const key of ROOT_KEYS) doc.getMap(key);

      const baseTs = Number(getMetaMap(doc).get('createdAt') ?? 0);
      ensureUiPreferences(doc, baseTs);
      const uiPreferences = doc.getMap('uiPreferences');
      if (!uiPreferences.has('tagColors')) uiPreferences.set('tagColors', {});

      const smartLists = doc.getMap<Y.Map<unknown>>('smartLists');
      for (const inner of smartLists.values()) {
        if (!inner.has('groupBy')) inner.set('groupBy', 'project');
        if (!inner.has('groupDirection')) inner.set('groupDirection', 'asc');
        if (!inner.has('sort')) inner.set('sort', [{ key: 'due', direction: 'asc' }]);
        if (!inner.has('showCompleted')) inner.set('showCompleted', true);
        if (!inner.has('showTrashed')) inner.set('showTrashed', false);
      }
    },
  },
  {
    from: 8,
    to: 9,
    apply: (doc) => {
      for (const key of ROOT_KEYS) doc.getMap(key);
      doc.getMap('taskArrangements');
      doc.getMap('selectedTaskArrangements');
    },
  },
  {
    from: 9,
    to: 10,
    apply: (doc) => {
      for (const key of ROOT_KEYS) doc.getMap(key);
    },
  },
  {
    from: 10,
    to: 11,
    apply: (doc) => {
      for (const key of ROOT_KEYS) doc.getMap(key);
    },
  },
];

export function runDefaultMigrations(doc: Y.Doc): void {
  runMigrations(doc, DEFAULT_MIGRATIONS, { targetVersion: SCHEMA_VERSION });
}
